import {
  Show,
  useRecordContext,
  useNotify,
  useRefresh,
  useDataProvider,
  TopToolbar,
  ListButton,
  useRedirect,
  Identifier,
  RaRecord,
} from 'react-admin';
import {
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  Chip,
  Tab,
  Tabs,
  Dialog,
  Breadcrumbs,
  Link,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  DragIndicator as DragIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';

const STATUS_COLORS = [
  { value: '#6B7280', label: 'Gray' },
  { value: '#EF4444', label: 'Red' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#10B981', label: 'Green' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#0EA5E9', label: 'Sky' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#A855F7', label: 'Violet' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#F43F5E', label: 'Rose' },
  { value: '#78716C', label: 'Stone' },
];

interface WorkflowStatus {
  id?: string;
  name: string;
  color: string;
  orderIndex: number;
  canDelete?: boolean;
}

interface WorkflowCategory {
  id?: string;
  name: string;
  orderIndex: number;
}

const StatusManager = ({ statuses, setStatuses, workflowId }: { statuses: WorkflowStatus[], setStatuses: (statuses: WorkflowStatus[]) => void, workflowId: string }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#3B82F6');
  const [editStatusName, setEditStatusName] = useState('');
  const [editStatusColor, setEditStatusColor] = useState('#3B82F6');

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) {
      notify('Status name is required', { type: 'warning' });
      return;
    }

    // Calculate orderIndex for new status (add at the end)
    const maxOrder = statuses.length > 0
      ? Math.max(...statuses.map(s => s.orderIndex))
      : 0;

    try {
      // Create status via API
      const { data: newStatus } = await dataProvider.create('task-statuses', {
        data: {
          workflowId: workflowId,
          name: newStatusName,
          color: newStatusColor,
          orderIndex: maxOrder + 1,
          canDelete: true,
        }
      });

      // Add to local state
      setStatuses([...statuses, newStatus]);
      setNewStatusName('');
      setNewStatusColor('#3B82F6');
      setAddDialogOpen(false);
      notify('Status added successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Failed to add status', { type: 'error' });
    }
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditStatusName(statuses[index].name);
    setEditStatusColor(statuses[index].color);
    setEditDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (editingIndex === null) {
      return;
    }
    if (!editStatusName.trim()) {
      notify('Status name is required', { type: 'warning' });
      return;
    }

    const statusToUpdate = statuses[editingIndex];

    try {
      // Update status via API
      await dataProvider.update('task-statuses', {
        id: statusToUpdate.id as Identifier,
        data: {
          name: editStatusName,
          color: editStatusColor,
        },
        previousData: statusToUpdate as RaRecord<Identifier>,
      });

      // Update local state
      const newStatuses = [...statuses];
      newStatuses[editingIndex] = {
        ...newStatuses[editingIndex],
        name: editStatusName,
        color: editStatusColor,
      };
      setStatuses(newStatuses);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Status updated successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to update status', { type: 'error' });
    }
  };

  const handleDeleteStatus = async () => {
    if (editingIndex === null) {
      return;
    }

    const statusToDelete = statuses[editingIndex];

    try {
      // Delete status via API
      await dataProvider.delete('task-statuses', {
        id: statusToDelete.id as Identifier,
        previousData: statusToDelete as RaRecord<Identifier>,
      });

      // Update local state
      const newStatuses = statuses.filter((_, i) => i !== editingIndex);
      setStatuses(newStatuses);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Status deleted successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to delete status', { type: 'error' });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Calculate new orderIndex using decimal positioning
    let newOrderIndex: number;
    const destIndex = result.destination.index;

    if (destIndex === 0) {
      // Moving to first position
      newOrderIndex = items[0].orderIndex / 2;
    } else if (destIndex === items.length - 1) {
      // Moving to last position
      newOrderIndex = items[items.length - 2].orderIndex + 1;
    } else {
      // Moving to middle position - average of neighbors
      const prevOrder = items[destIndex - 1].orderIndex;
      const nextOrder = items[destIndex + 1].orderIndex;
      newOrderIndex = (prevOrder + nextOrder) / 2;
    }

    reorderedItem.orderIndex = newOrderIndex;
    items[destIndex] = reorderedItem;

    // Update local state immediately for responsiveness
    setStatuses(items);

    // Update only the moved item via API
    try {
      await dataProvider.update('task-statuses', {
        id: reorderedItem.id as Identifier,
        data: { orderIndex: newOrderIndex },
        previousData: reorderedItem as RaRecord<Identifier>,
      });
      notify('Status order updated', { type: 'success' });
    } catch (error) {
      notify('Failed to update status order', { type: 'error' });
      // Revert on error
      refresh();
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
        >
          Add Status
        </Button>
      </Box>

      {/* Add Status Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Status Name"
              fullWidth
              value={newStatusName}
              onChange={(e) => setNewStatusName(e.target.value)}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={newStatusColor}
                onChange={(e) => setNewStatusColor(e.target.value)}
                label="Color"
              >
                {STATUS_COLORS.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color.value,
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddStatus} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Status Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Status Name"
              fullWidth
              value={editStatusName}
              onChange={(e) => setEditStatusName(e.target.value)}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={editStatusColor}
                onChange={(e) => setEditStatusColor(e.target.value)}
                label="Color"
              >
                {STATUS_COLORS.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color.value,
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          {editingIndex !== null && statuses[editingIndex]?.canDelete !== false && (
            <Button onClick={handleDeleteStatus} color="error">Delete</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="statuses">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {statuses.map((status, index) => (
                <Draggable
                  key={status.id || index}
                  draggableId={status.id || `status-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 1,
                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <CardContent
                        sx={{
                          py: 1,
                          '&:last-child': { pb: 1 },
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                        onClick={() => handleEditClick(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}>
                            <DragIcon sx={{ cursor: 'grab', color: 'text.secondary' }} />
                          </Box>

                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: status.color,
                              flexShrink: 0,
                            }}
                          />

                          <Typography sx={{ flex: 1 }}>
                            {status.name}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {statuses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No statuses defined. Add a status to get started.
        </Box>
      )}
    </Box>
  );
};

const CategoryManager = ({ categories, _workflowId }: { categories: WorkflowCategory[], _workflowId: string }) => {
  const navigate = useNavigate();
  const [localCategories, setLocalCategories] = useState(categories);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const handleCategoryClick = (cat: WorkflowCategory) => {
    if (cat.id) {
      navigate(`/task-categories/${cat.id}`);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      notify('Category name is required', { type: 'warning' });
      return;
    }
    // Calculate orderIndex for new category (add at the end)
    const maxOrder = localCategories.length > 0
      ? Math.max(...localCategories.map(c => c.orderIndex))
      : 0;
    try {
      const { data: newCategory } = await dataProvider.create('task-categories', {
        data: {
          workflowId: _workflowId,
          name: newCategoryName,
          orderIndex: maxOrder + 1,
        }
      });
      setLocalCategories([...localCategories, newCategory]);
      setNewCategoryName('');
      setAddDialogOpen(false);
      notify('Category added successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Failed to add category', { type: 'error' });
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const items = Array.from(localCategories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Calculate new orderIndex using decimal positioning
    let newOrderIndex: number;
    const destIndex = result.destination.index;
    if (destIndex === 0) {
      newOrderIndex = items[0].orderIndex / 2;
    } else if (destIndex === items.length - 1) {
      newOrderIndex = items[items.length - 2].orderIndex + 1;
    } else {
      const prevOrder = items[destIndex - 1].orderIndex;
      const nextOrder = items[destIndex + 1].orderIndex;
      newOrderIndex = (prevOrder + nextOrder) / 2;
    }
    reorderedItem.orderIndex = newOrderIndex;
    items[destIndex] = reorderedItem;
    setLocalCategories(items);

    // Update only the moved item via API
    try {
      await dataProvider.update('task-categories', {
        id: reorderedItem.id,
        data: { orderIndex: newOrderIndex },
        previousData: reorderedItem,
      });
      notify('Category order updated', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Failed to update category order', { type: 'error' });
      refresh();
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
        >
          Add Category
        </Button>
      </Box>
      {/* Add Category Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {localCategories.map((cat, index) => (
                <Draggable key={cat.id || index} draggableId={cat.id || `cat-${index}`} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 1,
                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 }, display: 'flex', alignItems: 'center' }}>
                        <Box {...provided.dragHandleProps} onClick={e => e.stopPropagation()}>
                          <DragIcon sx={{ cursor: 'grab', color: 'text.secondary', mr: 2 }} />
                        </Box>
                        <Typography sx={{ flex: 1 }}>{cat.name}</Typography>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      {localCategories.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No categories defined.
        </Box>
      )}
    </Box>
  );
};

const WorkflowShowContent = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromQuery = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return tab ? parseInt(tab, 10) : 0;
  };

  const [tabValue, setTabValue] = useState(getTabFromQuery());

  useEffect(() => {
    setTabValue(getTabFromQuery());
  }, [getTabFromQuery, location.search]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue.toString());
    navigate(`?${params.toString()}`, { replace: true });
  };

  const [statuses, setStatuses] = useState<WorkflowStatus[]>([]);
  const [categories, setCategories] = useState<WorkflowCategory[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [basicInfoDialogOpen, setBasicInfoDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  // Initialize state from record
  React.useEffect(() => {
    if (record) {
      // Sort statuses and categories by orderIndex
      const sortedStatuses = [...(record.statuses || [])].sort((a, b) => a.orderIndex - b.orderIndex);
      const sortedCategories = [...(record.categories || [])].sort((a, b) => a.orderIndex - b.orderIndex);

      setStatuses(sortedStatuses);
      setCategories(sortedCategories);
    }
  }, [record]);

  // Auto-save function - only sends the fields that are being updated
  const autoSave = async (updatedData: Partial<RaRecord<Identifier>>) => {
    if (!record || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      // Use dataProvider directly to ensure we only send what we want
      await dataProvider.update('workflows', {
        id: record.id,
        data: updatedData,
        previousData: record,
      });
      notify('Workflow updated successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Failed to update workflow', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };


  const handleEditBasicInfo = () => {
    setEditName(record?.name || '');
    setEditDescription(record?.description || '');
    setEditIsActive(record?.isActive ?? true);
    setBasicInfoDialogOpen(true);
  };

  const handleUpdateBasicInfo = async () => {
    if (!editName.trim()) {
      notify('Workflow name is required', { type: 'warning' });
      return;
    }

    // Update local record immediately for UI responsiveness
    if (record) {
      record.name = editName;
      record.description = editDescription;
      record.isActive = editIsActive;
    }

    await autoSave({
      name: editName,
      description: editDescription,
      isActive: editIsActive,
    });

    setBasicInfoDialogOpen(false);
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode, value: number, index: number }) => {
    return (
      <Box hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => redirect('/configuration')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Configuration
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => redirect('/workflows')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Workflows
        </Link>
        <Typography color="text.primary">{record?.name || 'Workflow'}</Typography>
      </Breadcrumbs>

      {isSaving && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'primary.main' }}>
          <Typography variant="body2">Saving changes...</Typography>
        </Box>
      )}

      {/* Basic Info Edit Dialog */}
      <Dialog open={basicInfoDialogOpen} onClose={() => setBasicInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Basic Info</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Workflow Name"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBasicInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBasicInfo} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Tabs value={tabValue} onChange={handleTabChange}>
        <Tab label="Basic Info" />
        <Tab label="Statuses" />
        <Tab label="Categories" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box>
          <Card sx={{ p: 2, position: 'relative' }}>
            <IconButton
              onClick={handleEditBasicInfo}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'action.hover',
                '&:hover': {
                  bgcolor: 'action.selected',
                }
              }}
              title="Edit Basic Info"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1">{record?.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Description</Typography>
                <Typography variant="body1">{record?.description || 'No description'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <br />
                <Chip
                  label={record?.isActive ? 'Active' : 'Inactive'}
                  color={record?.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Stack>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <StatusManager
          statuses={statuses}
          setStatuses={setStatuses}
          workflowId={record?.id.toString() || ''}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <CategoryManager
          categories={categories}
          _workflowId={record?.id?.toString() || ''}
        />
      </TabPanel>
    </Box>
  );
};

export const WorkflowShow = () => (
  <Show
    title="Workflow Details"
    actions={<WorkflowShowActions />}
  >
    <WorkflowShowContent />
  </Show>
);

const WorkflowShowActions = () => (
  <TopToolbar>
    <ListButton />
  </TopToolbar>
);
